import { Filter, Plus, Search } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Common/button';
import domainService from '../../services/domainService';
import { useInterviews } from '../../hooks/useInterviews';
import { NewInterviewModal } from '../../components/Interviews/NewInterviewModal';
import { InterviewEditModal } from '../../components/Interviews/InterviewEditModal';
import { Interview, NewInterviewData, InterviewUpdate } from '../../types';

const InterviewsList: React.FC = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [domainId, setDomainId] = useState<string | null>(null);
  const [isNewInterviewModalOpen, setIsNewInterviewModalOpen] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingInterview, setIsSavingInterview] = useState(false);

  // Fetch domain ID
  useEffect(() => {
    let mounted = true;
    const loadDomain = async () => {
      try {
        const domains = await domainService.getUserDomains();
        const activeDomainId = domains?.[0]?.id || null;
        if (mounted) setDomainId(activeDomainId);
      } catch (error) {
        console.error('Error loading domain:', error);
      }
    };
    loadDomain();
    return () => { mounted = false; };
  }, []);

  // Use the interviews hook
  const {
    interviews,
    isLoading: loading,
    error,
    createInterview,
    updateInterview,
  } = useInterviews(domainId || undefined);
  
  // Handle new interview creation
  const handleCreateInterview = async (data: Partial<NewInterviewData>) => {
    if (!domainId) {
      console.error('No domain ID available');
      return;
    }

    const newInterview = await createInterview({
      template_id: data.templateId || '',
      domain_id: domainId,
      name: data.title || null,
      status: 'Planned',
    });

    if (newInterview) {
      setIsNewInterviewModalOpen(false);
    }
  };

  // Handle interview update
  const handleUpdateInterview = async (interviewId: string, data: InterviewUpdate) => {
    setIsSavingInterview(true);
    try {
      const updated = await updateInterview(interviewId, data);
      if (updated) {
        setIsEditModalOpen(false);
        setSelectedInterview(null);
      }
    } finally {
      setIsSavingInterview(false);
    }
  };

  // Handle interview card click
  const handleInterviewClick = (interview: Interview) => {
    setSelectedInterview(interview);
    setIsEditModalOpen(true);
  };

  // Filter interviews
  const filteredInterviews = interviews.filter(interview => {
    const searchLower = searchQuery.toLowerCase();
    const name = interview.name?.toLowerCase() || '';
    const interviewee = interview.interviewee_name?.toLowerCase() || '';
    return name.includes(searchLower) || interviewee.includes(searchLower);
  });
  
  // Format date function
  const formatDate = (dateString: string): string => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <h1 className="text-2xl font-bold text-foreground">{t('interviewsPage.title')}</h1>
        
        <div className="flex space-x-3">
          <Button variant="outline" className="inline-flex items-center">
            <Filter className="h-4 w-4 mr-2" />
            {t('interviewsPage.filter')}
          </Button>
          <Button 
            className="inline-flex items-center"
            onClick={() => setIsNewInterviewModalOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('interviewsPage.newInterview')}
          </Button>
        </div>
      </div>
      
      <div className="relative max-w-md w-full">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-muted-foreground" />
        </div>
        <input
          type="text"
          placeholder={t('interviewsPage.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="block w-full pl-10 pr-3 py-2 border border-input rounded-md leading-5 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring sm:text-sm"
        />
      </div>
      
      {loading && (
        <div className="text-center text-muted-foreground">{t('interviewsPage.loading')}</div>
      )}
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded mb-4">{error}</div>
      )}
      {!loading && !error && filteredInterviews.length === 0 && (
        <div className="flex flex-col items-center justify-center border border-dashed border-border rounded-lg p-10 text-center text-muted-foreground">
          <h2 className="text-lg font-semibold text-foreground mb-2">{t('interviewsPage.noInterviews')}</h2>
          <p className="mb-4">{t('interviewsPage.noInterviewsDescription')}</p>
          <Button 
            className="inline-flex items-center"
            onClick={() => setIsNewInterviewModalOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('interviewsPage.createInterview')}
          </Button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredInterviews.map((interview) => (
          <div 
            key={interview.id} 
            className="bg-card text-card-foreground rounded-lg border border-border shadow-sm hover:shadow-md transition-shadow duration-300 cursor-pointer"
            onClick={() => handleInterviewClick(interview)}
          >
            <div className="p-6">
              <h2 className="text-lg font-semibold text-foreground mb-2">
                {interview.name || t('interviewsPage.interviewId', { id: interview.id.substring(0, 8) })}
              </h2>
              <div className="flex items-center text-sm text-muted-foreground mb-1">
                <UserIcon className="h-4 w-4 mr-2" />
                <span>{interview.interviewee_name || t('interviewsPage.notDefined')}</span>
              </div>
              <div className="flex items-center text-sm text-muted-foreground mb-4">
                <CalendarIcon className="h-4 w-4 mr-2" />
                <span>
                  {interview.interview_date 
                    ? formatDate(interview.interview_date) 
                    : t('interviewsPage.dateNotDefined')
                  }
                </span>
              </div>
              
              <div className="flex justify-between items-center mt-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  interview.status === 'Completed' 
                    ? 'bg-green-100 text-green-800'
                    : interview.status === 'In Progress'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {interview.status || 'Planifié'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t('interviewsPage.responsesCount', { count: interview.answers.length })}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Modals */}
      <NewInterviewModal
        isOpen={isNewInterviewModalOpen}
        onClose={() => setIsNewInterviewModalOpen(false)}
        onSubmit={handleCreateInterview}
        templateType="Cadrage"
      />

      <InterviewEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedInterview(null);
        }}
        interview={selectedInterview}
        onSave={handleUpdateInterview}
        isLoading={isSavingInterview}
      />
    </div>
  );
};

// Simple icon components
const UserIcon: React.FC<{className?: string}> = ({className}) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const CalendarIcon: React.FC<{className?: string}> = ({className}) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

export default InterviewsList;